import { useState } from 'react';

import { useLanguage } from '../lib/i18n/LanguageContext';
import { useSession } from '../lib/SessionContext';
import { SelectMenu } from './SelectMenu';

/**
 * Topbar org identity (LLMR-54).
 *
 * Always renders when the account has an org — even with exactly one, this
 * is what makes "which org am I in" visible at all. `me.organizations` used
 * to be silently truncated to whichever one the session happened to resolve
 * to, so a member invited into a second org while already owning their own
 * had no signal anywhere in the UI that the invite had even worked.
 *
 * A single org renders as plain text: there's nothing to switch TO, and a
 * dropdown with one disabled-looking option reads as broken, not calm.
 *
 * `variant="inline"` is for the mobile profile menu: one org stays plain
 * text; multiple orgs use the same SelectMenu dropdown as the topbar so the
 * menu does not grow into a long list. NavUserMenu ignores clicks/scroll on
 * the portaled select panel so nesting stays usable.
 */
export function OrgSwitcher({
  variant = 'select',
  className,
}: {
  variant?: 'select' | 'inline';
  className?: string;
}) {
  const { me, switchOrg } = useSession();
  const { dict } = useLanguage();
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState(false);

  const orgs = me?.organizations ?? [];
  if (orgs.length === 0) return null;

  const activeOrg = orgs.find((o) => o.org_id === me?.active_org_id) ?? orgs[0];
  if (!activeOrg) return null;

  const activeOrgId = activeOrg.org_id;
  const rootClass = ['org-switcher', className].filter(Boolean).join(' ');
  const selectOptions = orgs.map((o) => ({
    value: o.org_id,
    label: dict.orgSwitcher.optionLabel(o.name, dict.team.role[o.role]),
  }));

  async function handleChange(orgId: string) {
    if (orgId === activeOrgId) return;
    setError(false);
    setSwitching(true);
    try {
      await switchOrg(orgId);
      // switchOrg reloads the page on success — nothing left to do here.
    } catch {
      setSwitching(false);
      setError(true);
    }
  }

  if (variant === 'inline') {
    return (
      <div className={`${rootClass} org-switcher--inline`}>
        <p className="nav-user-panel-section-label">{dict.orgSwitcher.label}</p>
        {orgs.length === 1 ? (
          <p className="nav-user-panel-org-current">{activeOrg.name}</p>
        ) : (
          <SelectMenu
            className="org-switcher org-switcher--inline-select"
            aria-label={dict.orgSwitcher.label}
            value={activeOrg.org_id}
            disabled={switching}
            options={selectOptions}
            onChange={(v) => void handleChange(v)}
          />
        )}
        {error && <p className="nav-user-panel-error">{dict.orgSwitcher.switchError}</p>}
      </div>
    );
  }

  if (orgs.length === 1) {
    return (
      <span className={`org-switcher org-switcher--single${className ? ` ${className}` : ''}`} title={activeOrg.name}>
        {activeOrg.name}
      </span>
    );
  }

  return (
    <div className={rootClass}>
      <SelectMenu
        className="org-switcher"
        aria-label={dict.orgSwitcher.label}
        value={activeOrg.org_id}
        disabled={switching}
        options={selectOptions}
        onChange={(v) => void handleChange(v)}
      />
      {error && <span className="org-switcher-error">{dict.orgSwitcher.switchError}</span>}
    </div>
  );
}
